<template>
  <v-container class="mt-8" fluid>
    <v-row justify="center">
      <v-col cols="12" sm="8" md="6" lg="4">
        <v-card 
          class="mx-auto call-card" 
          elevation="8"
          rounded="lg"
        >
          <!-- Header con gradiente -->
          <v-card-title class="call-header text-white pa-6">
            <div class="d-flex align-center">
              <v-icon class="mr-3" size="32" color="red">mdi-phone</v-icon>
              <div>
                <h2 class="text-h5 font-weight-bold mb-0">Llamadas en Vivo</h2>
                <p class="text-body-2 mb-0 text-white-secondary">
                  Sistema de llamadas Twilio
                </p>
              </div>
            </div>
          </v-card-title>

          <v-card-text class="pa-6">
            <!-- Campo de número mejorado -->
            <v-text-field
              v-model="numero"
              label="Número de teléfono"
              placeholder="Ej: 8123456789"
              prepend-inner-icon="mdi-phone-outline"
              variant="outlined"
              color="primary"
              class="mb-4"
              :rules="[rules.required, rules.phone]"
              :error-messages="errorMessages"
              clearable
              counter="10"
            >
              <template v-slot:prepend>
                <v-chip 
                  small 
                  color="primary" 
                  text-color="white"
                  class="mr-2"
                >
                  +521
                </v-chip>
              </template>
            </v-text-field>

            <!-- Estado de la llamada -->
            <v-alert
              v-if="estadoLlamada"
              :type="estadoLlamada.tipo"
              class="mb-4"
              variant="tonal"
            >
            	{{ estadoLlamada.mensaje }}
            </v-alert>

            <!-- Botones de acción -->
            <div class="d-flex flex-column ga-3">
              <v-btn
                @click="iniciar"
                :disabled="!numero || llamadaActiva || cargando"
                :loading="cargando"
                color="success"
                size="large"
                variant="flat"
                block
                rounded
                class="text-none font-weight-bold mb-4"
              >
                <v-icon class="mr-2">mdi-phone-dial</v-icon>
                {{ llamadaActiva ? 'Llamada en curso' : 'Iniciar Llamada' }}
              </v-btn>

              <v-btn
                @click="colgar"
                :disabled="!llamadaActiva || cargando"
                color="error"
                size="large"
                variant="flat"
                block
                rounded
                class="text-none font-weight-bold"
              >
                <v-icon class="mr-2">mdi-phone-hangup</v-icon>
                Colgar
              </v-btn>
            </div>
          </v-card-text>

          <!-- Footer con información -->
          <v-card-actions class="pa-6 pt-0">
            <v-chip
              :color="llamadaActiva ? 'success' : 'grey'"
              variant="tonal"
              size="small"
            >
              <v-icon class="mr-1" size="16">
                {{ llamadaActiva ? 'mdi-phone-in-talk' : 'mdi-phone-off' }}
              </v-icon>
              {{ llamadaActiva ? 'Conectado' : 'Desconectado' }}
            </v-chip>
            <v-spacer />
            <v-btn
              icon
              variant="text"
              size="small"
              @click="limpiarCampos"
              :disabled="llamadaActiva"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { inicializarTwilio, llamarANumero, colgarLlamada } from '@/mixins/twilioClient.js';

export default {
  name: 'ComponenteLlamadas',
  data() {
    return {
      numero: '',
      llamadaActiva: false,
      cargando: false,
      estadoLlamada: null,
      errorMessages: [],
      rules: {
        required: value => !!value || 'El número es requerido',
        phone: value => {
          const pattern = /^[0-9]{10}$/;
          return pattern.test(value) || 'Ingresa un número válido de 10 dígitos';
        }
      }
    };
  },
  async mounted() {
    // try {
    //   await inicializarTwilio();
    //   this.mostrarEstado('success', 'Sistema de llamadas inicializado correctamente');
    // } catch (error) {
    //   this.mostrarEstado('error', 'Error al inicializar el sistema de llamadas');
    // }

    try {
		  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
		  console.log('✅ Acceso al micrófono concedido');
		  stream.getTracks().forEach(track => track.stop());
		} catch (error) {
		  console.error('❌ Acceso al micrófono denegado:', error.message);
		}

  },
  methods: {
    async iniciar() {
		  if (!this.validarNumero()) return;

		  this.cargando = true;
		  this.errorMessages = [];

		  try {
		    // asegúrate que el device está inicializado (reintento implícito)
		    await inicializarTwilio();

		    await llamarANumero('+521' + this.numero);
		    this.llamadaActiva = true;
		    this.mostrarEstado('success', `Llamando al +521${this.numero}...`);
		  } catch (error) {
		    this.mostrarEstado('error', 'Error al iniciar la llamada');
		    console.error('Error en llamada:', error);
		  } finally {
		    this.cargando = false;
		  }
		},

    async colgar() {
      this.cargando = true;
      
      try {
        await colgarLlamada();
        this.llamadaActiva = false;
        this.mostrarEstado('info', 'Llamada terminada');
      } catch (error) {
        this.mostrarEstado('error', 'Error al colgar la llamada');
        console.error('Error al colgar:', error);
      } finally {
        this.cargando = false;
      }
    },
    
    validarNumero() {
      const esValido = this.rules.phone(this.numero) === true && 
                       this.rules.required(this.numero) === true;
      
      if (!esValido) {
        this.errorMessages = ['Por favor ingresa un número válido'];
        return false;
      }
      
      return true;
    },
    
    mostrarEstado(tipo, mensaje) {
      this.estadoLlamada = { tipo, mensaje };
      
      // Auto-ocultar después de 5 segundos para mensajes de éxito/info
      if (tipo === 'success' || tipo === 'info') {
        setTimeout(() => {
          this.estadoLlamada = null;
        }, 5000);
      }
    },
    
    limpiarCampos() {
      this.numero = '';
      this.errorMessages = [];
      this.estadoLlamada = null;
    }
  }
};
</script>

<style scoped>
	.call-card {
	}

	.call-header {
	}

	.call-header::before {
	  content: '';
	  position: absolute;
	  top: 0;
	  left: 0;
	  right: 0;
	  bottom: 0;
	}

	.call-header > * {
	  position: relative;
	  z-index: 1;
	}

	.v-card-text {
	  background: white;
	}

	.text-white-secondary {
	  opacity: 0.8;
	}

	/* Animaciones suaves */
	.v-btn {
	  transition: all 0.3s ease;
	}

	.v-btn:hover {
	  transform: translateY(-2px);
	  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	}

	.v-btn:disabled {
	  transform: none !important;
	}

	/* Efectos de enfoque */
	.v-text-field:focus-within {
	  transform: translateY(-1px);
	  transition: transform 0.2s ease;
	}

	/* Responsive */
	@media (max-width: 600px) {
	  .call-header {
	    padding: 1rem !important;
	  }
	  
	  .v-card-text {
	    padding: 1rem !important;
	  }
	}
</style>